import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';

/**
 * Contexto con el que se resuelven las opciones de los campos que no las llevan en línea:
 * - configuration: objeto de configuración de origen (Integration.configuration), para optionsSource por clave
 * - persons: cliente y afectados del expediente (optionsSource: 'persons')
 * - notificationNumbers: avisos del expediente (type: 'notificationNumber')
 */
export interface DynamicFieldsContext {
  configuration?: any;
  persons?: any[];
  notificationNumbers?: any[];
}

/**
 * Motor de formularios dinámicos definidos por configuración JSON ({ fields: [...] }).
 * Lo usan tanto el eventForm de las integraciones como el formulario propio del tipo de evento
 * (CaseLogType/ContractCaseLogType.fieldsConfiguration). Es lógica pura: recibe los campos, el
 * objeto de valores y el contexto, y no guarda estado propio.
 */
@Injectable({ providedIn: 'root' })
export class DynamicFieldsService {

  /**
   * Extrae la lista de campos de una configuración JSON en texto.
   * Devuelve [] ante un JSON inválido o sin fields: un error de configuración no debe romper la pantalla.
   */
  parseFields(configuration: string | null | undefined): any[] {
    if (configuration == null || configuration.trim() == '') return [];
    try {
      let parsed = JSON.parse(configuration);
      let fields = parsed?.fields ?? parsed?.eventForm?.fields;
      return Array.isArray(fields) ? fields : [];
    } catch (error) {
      console.error('Configuración de campos dinámicos inválida', error);
      return [];
    }
  }

  //evalúa las condiciones showWhen (AND) de un campo contra los valores actuales
  isVisible(field: any, data: any): boolean {
    if (!field.showWhen || field.showWhen.length == 0) return true;
    return field.showWhen.every((condition: any) => {
      let current = data[condition.field];
      if (condition.in) return condition.in.includes(current);
      if (condition.equals !== undefined) return current == condition.equals;
      if (condition.truthy) return !!current;
      return true;
    });
  }

  //resuelve las opciones de un select: en línea, clave de la configuración o fuente runtime (persons)
  getOptions(field: any, data: any, context: DynamicFieldsContext): any[] {
    let options: any[] = [];
    if (field.options) {
      options = field.options;
    } else if (field.optionsSource) {
      if (field.optionsSource == 'persons') options = context.persons ?? [];
      else options = context.configuration?.[field.optionsSource] ?? [];
    }
    if (field.filterBy) {
      let parentValue = data[field.filterBy];
      options = options.filter((option: any) => option[field.filterBy] == parentValue);
    }
    return options;
  }

  /**
   * Al cambiar un campo: limpia los que dejan de ser visibles (incluidas dependencias transitivas)
   * y autoselecciona la primera opción en los selects visibles marcados con defaultFirst.
   */
  applyChange(fields: any[], changedField: any | null, data: any, context: DynamicFieldsContext) {
    if (!fields) return;

    let changed = true;
    while (changed) {
      changed = false;
      fields.forEach((field: any) => {
        if (changedField && field.code == changedField.code) return;
        if (!this.isVisible(field, data) && data[field.code] !== undefined) {
          delete data[field.code];
          changed = true;
          return;
        }
        //un select dependiente sigue siendo visible al cambiar su padre, pero su valor puede haber
        //dejado de estar entre las opciones: si no se limpia, queda un valor que ya no se ve en el
        //desplegable y que aun así se guardaría y saldría en el resumen
        if (field.filterBy && data[field.code] != null && data[field.code] !== '') {
          let options = this.getOptions(field, data, context);
          if (!options.some((option: any) => option[field.optionValue ?? 'value'] == data[field.code])) {
            delete data[field.code];
            changed = true;
          }
        }
      });
    }

    fields.forEach((field: any) => {
      if (field.defaultFirst && this.isVisible(field, data) &&
        (data[field.code] == null || data[field.code] === '')) {
        let options = this.getOptions(field, data, context);
        if (options.length > 0) {
          data[field.code] = options[0][field.optionValue ?? 'value'];
        }
      }
    });
  }

  //valor legible de un campo para el resumen (label de opción, displayInfo, fecha formateada, Sí/No)
  getSummaryValue(field: any, data: any, context: DynamicFieldsContext): string {
    let value = data[field.code];
    if (value == null || value === '') return '';
    switch (field.type) {
      case 'checkbox':
        //solo se refleja en el resumen cuando está marcado
        return value ? 'Sí' : '';
      case 'date':
        return formatDate(value, 'dd/MM/yyyy', 'es-ES');
      case 'datetime':
        return formatDate(value, 'dd/MM/yyyy HH:mm', 'es-ES');
      case 'notificationNumber': {
        let notificationNumber = (context.notificationNumbers ?? []).find((m: any) => m.code == value);
        return notificationNumber?.displayInfo ?? value;
      }
      case 'select': {
        let options = this.getOptions(field, data, context);
        let option = options.find((o: any) => o[field.optionValue ?? 'value'] == value);
        return option ? option[field.optionLabel ?? 'label'] : value;
      }
      default:
        return '' + value;
    }
  }

  //construye el resumen HTML que se antepone al mensaje a partir de los campos visibles
  buildSummary(fields: any[], data: any, context: DynamicFieldsContext): string {
    if (!fields || fields.length == 0) return '';
    let visibleFields = fields.filter((field: any) => this.isVisible(field, data));

    //cabecera: campos marcados como isHeader unidos por ": "
    let headerParts = visibleFields
      .filter((field: any) => field.isHeader)
      .map((field: any) => this.getSummaryValue(field, data, context))
      .filter((value: string) => value != '');

    let summary = '';
    if (headerParts.length > 0) summary += '<b>' + headerParts.join(': ') + '</b><br>';

    //líneas etiqueta: valor por cada campo visible incluible con valor
    visibleFields.forEach((field: any) => {
      if (field.isHeader) return;
      if (field.includeInSummary === false) return;
      if (field.type == 'file-upload' || field.type == 'files-card') return;
      let value = this.getSummaryValue(field, data, context);
      if (value == '') return;
      summary += (field.summaryLabel ?? field.label) + ': ' + value + '<br>';
    });

    if (summary != '') summary += '<br>';
    return summary;
  }

  /**
   * Valida que los campos requeridos visibles tengan valor.
   * Devuelve el label del primer campo sin rellenar, o null si todo está correcto.
   */
  getFirstMissingRequired(fields: any[], data: any): string | null {
    if (!fields) return null;
    for (let field of fields) {
      if (!field.required || !this.isVisible(field, data)) continue;
      //los adjuntos (file-upload/files-card) se guardan siempre en data.files
      let value = (field.type == 'file-upload' || field.type == 'files-card')
        ? data.files
        : data[field.code];
      if (value == null || value === '' || (Array.isArray(value) && value.length == 0)) {
        return field.label;
      }
    }
    return null;
  }
}
